import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import moment from 'moment'
import 'photoswipe/dist/photoswipe.css'
import { Gallery, Item } from 'react-photoswipe-gallery'

import styles from './styles.module.scss'

import { hideDocumentPreview } from '../../../store/documents/actions'
import { getEmployeeDocument, getDocumentFileBlob, changeReviewStatusDocument, downloadDocumentFile, sendDocumentReminder } from '../../../api'
import useNotify from '../../../hooks/useNotify'

import { CrossIcon } from '../Icons'
import DocumentIcon from '../../Icons/DocumentIcon'

const BellIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={16}
    height={16}
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth={2}
    aria-hidden="true"
    className="lucide lucide-bell h-4 w-4"
    data-tsd-source="/src/components/docs/document-drawer.tsx:207:15"
    viewBox="0 0 24 24"
    {...props}
  >
    <path d="M10.268 21a2 2 0 0 0 3.464 0M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
  </svg>
)

const DownloadIcon = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      aria-hidden="true"
      {...props}
    >
      <path d="M12 15V3M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m7 10 5 5 5-5" />
    </svg>
  )
}

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M5 13l4 4L19 7" stroke="#32BF1F" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const RejectIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M18 6 6 18M6 6l12 12" stroke="#DE4343" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ACTIVITY_ICON_COLORS = {
  assigned: '#0085FF',
  opened: '#0085FF',
  acknowledged: '#32BF1F',
  approved: '#32BF1F',
  signed: '#32BF1F',
  reminder: '#808F94',
}

const getStatusColors = (status) => {
  const value = (status || '').toLowerCase()
  if (value.includes('valid') || value.includes('acknowledged') || value.includes('approved') || value.includes('signed')) {
    return { background: '#D1F9CB', text: '#32BF1F', border: '#BEF3B7' }
  }
  if (value.includes('seen')) {
    return { background: '#ECF0FF', text: '#6D75E8', border: '#D3D6FA' }
  }
  if (value.includes('pending') || value.includes('required') || value.includes('review') || value.includes('awaiting')) {
    return { background: '#FFD4A5', text: '#D77000', border: '#E7C88C' }
  }
  if (value.includes('declined') || value.includes('overdue') || value.includes('expired')) {
    return { background: '#FFBDBD', text: '#D70000', border: '#F77C7C' }
  }
  return { background: '#E9E9E9', text: '#919191', border: '#C6C6C6' }
}

const formatDate = (date, withTime = false) => {
  if (!date) return null
  return moment(date).format(withTime ? 'MMM D, HH:mm' : 'MMM D, YYYY')
}

const getFilePreviewType = (file) => {
  const extension = (file.name || '').split('.').pop().toLowerCase()
  const mimeType = file.mime_type || file.content_type || file.type || ''
  if (mimeType.startsWith('image/') || /\.(avif|bmp|gif|jpe?g|png|svg|tiff?|webp)$/i.test(file.name || '')) {
    return 'image'
  }
  if (mimeType === 'application/pdf' || extension === 'pdf') {
    return 'pdf'
  }
  return 'download'
}

const secondsToMinutes = (seconds) => {
  if (!seconds) return '0m'
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m${remainingSeconds > 0 ? ` ${remainingSeconds}s` : ''}`
}

const getActionDescription = (t, action) => {
  if (!action.required) {
    return t('Not required')
  }
  const parts = [t('Required')]
  if (action.mode) {
    parts.push(action.mode)
  } else if (action.completed_at) {
    parts.push(formatDate(action.completed_at, true))
  }
  return parts.join(' · ')
}

const initialData = {
  employee: {},
  document: {
    type: {},
    category: {},
    requirements: {},
    status: {},
  },
  activity: [],
  employee_actions: [],
  reading: {},
}

const DocumentPreview = ({ companyId }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const notify = useNotify()

  const { show, data: { employeeId, documentId } } = useSelector(state => state.documents.preview)
  const [showLayout, setShowLayout] = useState(false)

  const [data, setData] = useState(initialData)
  const [imageFiles, setImageFiles] = useState([])

  useEffect(() => {
    if (show) {
      setTimeout(() => {
        setShowLayout(true)
      }, 100)
    } else {
      setShowLayout(false)
      setData(initialData)
      setImageFiles(previous => {
        previous.forEach(photo => window.URL.revokeObjectURL(photo.url))
        return []
      })
    }
  }, [show])

  useEffect(() => {
    if (companyId && show && employeeId && documentId) {
      fetchDocument()
    }
  }, [companyId, show, employeeId, documentId])

  const fetchDocument = async () => {
    const res = await getEmployeeDocument(companyId, employeeId, documentId)
    if (res) {
      setData(res)
      loadImageFiles(res.document)
    }
  }

  const loadImageFiles = async (document) => {
    const files = (document.files || []).filter(file => getFilePreviewType(file) === 'image')
    if (!files.length) {
      setImageFiles([])
      return
    }
    setImageFiles(files)
  }

  const handleClose = () => {
    dispatch(hideDocumentPreview())
  }

  const handleChangeReviewStatus = async (status) => {
    const res = await changeReviewStatusDocument(companyId, document.id, {
      review_status: status,
      document_employee_file_ids: document.files.map(file => file.document_employee_file_id),
    })
    if (res) {
      fetchDocument()
      window.dispatchEvent(
        new CustomEvent('documents-updated', {detail: {}})
      )
    }
  }

  const handleDownload = () => {
    document.files.forEach(file => {
      downloadDocumentFile(companyId, document.id, file.id)
    })
  }

  const handleOpenPdfFile = async (file) => {
    const previewWindow = window.open('', '_blank')
    try {
      const blob = await getDocumentFileBlob(companyId, document.id, file.id)
      const url = window.URL.createObjectURL(blob)
      if (previewWindow) {
        previewWindow.opener = null
        previewWindow.location = url
      }
    } catch (error) {
      if (previewWindow) {
        previewWindow.close()
      }
      notify.show(t('Unable to preview file'))
    }
  }

  const handleDownloadFile = (file) => {
    downloadDocumentFile(companyId, document.id, file.id)
  }

  const handleSendReminder = async () => {
    const res = await sendDocumentReminder(companyId, [{ document_id: documentId, employee_id: employeeId }])
    if (res?.data?.sent?.length) {
      notify.show(t('Reminder sent successfully'))
    }
  }

  const { document, employee, employee_actions: employeeActions, reading, activity } = data
  const statusColors = getStatusColors(document.status?.value)
  const uploadedActivity = activity.find(entry => entry.type === 'assigned') || activity[0]

  return (
    <>
      {
        show
          ? <div className={cn(styles.layout, {[styles.active]: showLayout})} onClick={handleClose} />
          : null
      }
      <div className={cn(styles.container, {[styles.active]: show})}>
        <div className={styles.header}>
          <div>
            <div className={styles.headerDescription}>{document.type.name}</div>
            <div className={styles.headerTitle}>{document.name} — {employee.full_name}</div>
            {
              document.status ? (
                <div
                  className={styles.statusBadge}
                  style={{ backgroundColor: statusColors.background, color: statusColors.text, border: `1px solid ${statusColors.border}` }}
                >
                  <div className={styles.statusDot} style={{ backgroundColor: statusColors.text }} />
                  { t(document.status.label) }
                </div>
              ) : null
            }
          </div>
          <button type="button" className={styles.closeButton} onClick={handleClose}>
            <CrossIcon />
          </button>
        </div>
        <div className={styles.actionsRow}>
          <button type="button" className={styles.actionButton} onClick={handleSendReminder}>
            <BellIcon />
            { t('Send reminder') }
          </button>
          {
            document.has_files
              ? <button type="button" className={styles.actionButton} onClick={handleDownload}>
                  <DownloadIcon />
                  { t('Download') }
                </button>
              : null
          }
          {
            document.requirements?.review && document.has_files ? (
              <>
                <button type="button" className={styles.actionButton} onClick={() => handleChangeReviewStatus('approved')}>
                  <CheckIcon />
                  { t('Approve') }
                </button>
                <button type="button" className={styles.actionButton} onClick={() => handleChangeReviewStatus('rejected')}>
                  <RejectIcon />
                  { t('Reject') }
                </button>
              </>
            ) : null
          }
        </div>
        <div className={styles.content}>
          <div className={styles.infoGrid}>
            <div className={styles.infoCell}>
              <div className={styles.infoLabel}>{ t('Assigned to') }</div>
              <div className={styles.assignedTo}>
                <div className={styles.avatar}>{ `${(employee.name || '').charAt(0)}${(employee.surname || '').charAt(0)}`.toUpperCase() }</div>
                <div>
                  <div className={styles.infoValue}>{ employee.full_name }</div>
                  <div className={styles.infoSubValue}>
                    { [employee.company_role, employee.status === '1' ? t('Active') : t('Inactive')].filter(Boolean).join(' · ') }
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.infoCell}>
              <div className={styles.infoLabel}>{ t('Place') }</div>
              <div className={styles.infoValue}>{ document.place?.name || '-' }</div>
            </div>
            <div className={styles.infoCell}>
              <div className={styles.infoLabel}>{ t('Effective') }</div>
              <div className={styles.infoValue}>{ formatDate(document.effective_date) || t('No effective date') }</div>
            </div>
            <div className={styles.infoCell}>
              <div className={styles.infoLabel}>{ t('Expiration') }</div>
              <div className={styles.infoValue}>{ document.expiration_label || t('No expiration') }</div>
            </div>
            <div className={styles.infoCell}>
              <div className={styles.infoLabel}>{ t('Due date') }</div>
              <div className={styles.infoValue}>{ formatDate(document.due_date_for_action) || '-' }</div>
            </div>
            <div className={styles.infoCell}>
              <div className={styles.infoLabel}>{ t('Uploaded') }</div>
              <div className={styles.infoValue}>{ formatDate(uploadedActivity?.created_at) || '-' }</div>
              {
                document.uploaded_by?.name ? (
                  <div className={styles.infoSubValue}>{ t('by {{name}}', { name: document.uploaded_by.name }) }</div>
                ) : null
              }
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeaderRow}>
              <div className={styles.sectionTitle}>{ t('Document files') }</div>
            </div>
            {
              document.has_files && document.files?.length
                ? (
                  <Gallery>
                    <div className={styles.fileGallery}>
                      {
                        imageFiles.map(photo => (
                          <Item
                            key={photo.id}
                            original={photo.url}
                            thumbnail={photo.url}
                            width={photo.width}
                            height={photo.height}
                            alt={photo.name}
                          >
                            {({ ref, open }) => (
                              <button type="button" className={styles.fileThumbButton} ref={ref} onClick={open} title={photo.name}>
                                <img src={photo.url} alt={photo.name} className={styles.fileThumbImage} />
                              </button>
                            )}
                          </Item>
                        ))
                      }
                      {
                        document.files.filter(file => getFilePreviewType(file) !== 'image').map(file => {
                          const isPdf = getFilePreviewType(file) === 'pdf'
                          return (
                            <button
                              key={file.id}
                              type="button"
                              className={styles.fileRowButton}
                              onClick={() => isPdf ? handleOpenPdfFile(file) : handleDownloadFile(file)}
                              title={file.name}
                            >
                              { isPdf ? <DocumentIcon width={16} height={20} color="#808F94" /> : <DownloadIcon width={18} height={16} /> }
                              <div className={styles.fileName}>{ file.name }</div>
                            </button>
                          )
                        })
                      }
                    </div>
                  </Gallery>
                )
                : <div className={styles.noFileBox}>{ t('No file uploaded yet') }</div>
            }
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>{ t('Employee actions') }</div>
            {
              employeeActions.map(action => {
                const actionColors = getStatusColors(action.status)
                return (
                  <React.Fragment key={action.type}>
                    <div className={styles.actionRow}>
                      <div className={styles.actionInfo}>
                        <div className={styles.actionLabel}>{ t(action.label) }</div>
                        <div className={styles.actionDescription}>{ getActionDescription(t, action) }</div>
                      </div>
                      <div
                        className={styles.statusBadge}
                        style={{ backgroundColor: actionColors.background, color: actionColors.text, border: `1px solid ${actionColors.border}` }}
                      >
                        <div className={styles.statusDot} style={{ backgroundColor: actionColors.text }} />
                        { t(action.status) }
                      </div>
                    </div>
                    {
                      action.type === 'viewing' ? (
                        <div className={styles.readingGrid}>
                          {/* <div className={styles.infoCell}>
                            <div className={styles.infoLabel}>{ t('First seen') }</div>
                            <div className={styles.infoValue}>{ formatDate(reading.first_seen_at, true) || '-' }</div>
                          </div> */}
                          <div className={styles.infoCell}>
                            <div className={styles.infoLabel}>{ t('Last seen') }</div>
                            <div className={styles.infoValue}>{ formatDate(reading.first_seen_at, true) || '-' }</div>
                          </div>
                          <div className={styles.infoCell}>
                            <div className={styles.infoLabel}>{ t('Reading time') }</div>
                            <div className={styles.infoValue}>{ reading.progress?.reading_time_seconds ? secondsToMinutes(reading.progress?.reading_time_seconds) : t('Not tracked') }</div>
                          </div>
                        </div>
                      ) : null
                    }
                  </React.Fragment>
                )
              })
            }
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>{ t('Activity') }</div>
            <div className={styles.timeline}>
              {
                activity.map((entry, index) => (
                  <div key={index} className={styles.timelineRow}>
                    <div className={styles.timelineDot} style={{ backgroundColor: ACTIVITY_ICON_COLORS[entry.type] || '#808F94' }} />
                    <div className={styles.timelineInfo}>
                      <div className={styles.timelineLabel}>{ t(entry.label) }</div>
                      <div className={styles.timelineDate}>
                        { formatDate(entry.created_at, true) } { entry.employee?.name ? `· ${entry.employee.name}` : '' }
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default DocumentPreview

